import { withNamespaces } from "react-i18next";
import { Button, Card, CardBody, Col, Container, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { EMPTY_LIST } from "src/common/constants";
import NewProductModal from "../create-modal";
import { useCallback, useMemo, useState } from "react";
import DeleteProductModal from "../delete-modal";
import ProductQuickViewModal from "../detail-modal";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import { useHistory, useLocation } from "react-router-dom";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import FilteringComponent from "./components/FilteringComponent";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';
import { DDSTypes } from "src/modules/3rd-party-management/constants";
import TableColumnCustomizer from "src/modules/3rd-party-management/components/TableColumnCustomizer";
import useTableColumnCustomizer from "src/modules/3rd-party-management/components/TableColumnCustomizer/CustomizerHook";

const eudrDefaultCols = [
    'id',
    'name',
    'productName',
    'lotNumber',
    'articleNumber',
    'internalSku',
    'internalProductId',
    'importFromCountry',
    'exportToCountry',
    'importDate',   
    'metricTonnes',
    'is_eudr',
    'ddsReferenceNumber',
    'ddsVerificationNumber',
    'statementValidation',
    'hsCode',
    'knCode',
    // 'containerNumber',
    // 'shipmentNumber',
    // 'description',
    'actions'
];

const ProductList = ({
    t,
    loadingSupplier,
    industryIds,
    supplierId,
    supplierInfos,
    readOnly
}) => {
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });
    const [showNewProductModal, setShowNewProductModal] = useState(false);
    const [selectedProductToEdit, setSelectedProductToEdit] = useState(null);
    const [selectedProductToDelete, setSelectedProductToDelete] = useState(null);
    const [selectedProductToShow, setSelectedProductToShow] = useState(null);

    const history = useHistory();
    const location = useLocation();
    const dateUtils = new DateUtils();

    const {
        isFetching: productsAreFetching,
        isFetched: productsFetched,
        data: products,
        refetch: refetchProductsList
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-products-list', 
            supplierId,
            filters
        ],
        queryFn: async () => {
            const service = SupplierProductService.getInstance();

            return await service.list(supplierId, filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error'
            });
        }
    });

    const handleFetchCountriesQuery = useQuery({
        queryKey: ['3rd-party-management-fetch-countries-list'],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountries();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching locations.'), {
                type: 'error'
            });
        }
    });

    const eudrAllColumns = useMemo(() => {
        return [
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                style: {
                    width: '80px'
                }
            },
            {
                dataField: "name",
                text: t("NACE Code"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    return `${cellContent.class} - ${t(cellContent.name)}`
                }
            },
            {
                dataField: "productName",
                text: t("Product Name"),
                sort: true,
                style: {
                    width: '200px'
                }
            },
            {
                dataField: "lotNumber",
                text: t("Lot Number"),
                sort: true
            },
            {
                dataField: "internalProductId",
                text: t("Internal Product ID"),
                sort: true
            },
            {
                dataField: "articleNumber",
                text: t("Article Number"),
                sort: false,
                locked: true,
                formatter: (_cell, row) => row?.articleNumber || row?.article_number || '---',
            },
            {
                dataField: "internalSku",
                text: t("Internal SKU"),
                sort: false,
                locked: true,
                formatter: (_cell, row) => row?.internalSku || row?.internal_sku || '---',
            },
            {
                dataField: "importDate",
                text: t("Date of Import"),
                sort: true,
                formatter: (cellContent) => {
                    return (
                        dateUtils.convertDateToDate(cellContent, INTERNATIONAL_DATE_FORMAT)
                    )
                }
            },
            {
                dataField: "importFromCountry",
                text: t("Import Country"),
                sort: true,
                formatter: (cellContent) => {
                    return t(cellContent?.name)
                }
            },
            {
                dataField: "exportDate",
                text: t("Date of Export"),
                sort: true,
                formatter: (cellContent) => {
                    return (
                        dateUtils.convertDateToDate(cellContent, INTERNATIONAL_DATE_FORMAT)
                    )
                }
            },
            {
                dataField: "exportToCountry",
                text: t("Export Country"),
                sort: true,
                formatter: (cellContent) => {
                    return t(cellContent?.name)
                }
            },
            {
                dataField: "metricTonnes",
                text: t("Quantity (Metric Tonnes)"),
                sort: true
            },
            {
                dataField: "originCountry",
                text: t("Country of Origin"),
                sort: true,
                formatter: (cellContent) => {
                    return t(cellContent?.name)
                }
            },
            {
                dataField: "ddsDeclarationType",
                text: t("DDS Declaration Type"),
                sort: false,
                formatter: (cellContent) => {
                    return t(DDSTypes[cellContent]?.title)
                }
            },
            {
                dataField: "is_eudr",
                text: t("EUDR Product"),
                sort: false,
                formatter: (cellContent) => {
                    if (!cellContent) {
                        return (
                            <span>
                                <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                {t('No')}
                            </span>
                        )
                    }
                    return (
                        <span>
                            <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                            {t('Yes')}
                        </span>
                    )
                }
            },
            ...(isFeatureEnabledForActiveModule('PPWR') ? [{
                dataField: "is_ppwr",
                text: t("PPWR Product"),
                sort: true,
                formatter: (cellContent) => {
                    if (!cellContent) {
                        return (
                            <span>
                                <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                {t('No')}
                            </span>
                        )
                    }
                    return (
                        <span>
                            <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                            {t('Yes')}
                        </span>
                    )
                }
            }] : []),
            {
                dataField: "ddsReferenceNumber",
                text: t("DDS Reference Number"),
                sort: true
            },
            {
                dataField: "ddsVerificationNumber",
                text: t("DDS Verification Number"),
                sort: true
            },
            {
                dataField: "statementValidation",
                text: t("DDS Statement Status"),
                sort: true,
                formatter: (cellContent, { ddsDeclarationType }) => {
                    if (ddsDeclarationType === 'provide_reference_numbers') {
                        if (cellContent === false) {
                            return (
                                <span>
                                    <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                    {t('Invalid')}
                                </span>
                            )
                        }
                        else if (cellContent === true) {
                            return (
                                <span>
                                    <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                                    {t('Valid')}
                                </span>
                            )
                        }
                        else {
                            return (
                                <span>
                                    {t('Validating')}...
                                </span>
                            )
                        }
                    }

                    return '-'
                }
            },
            {
                dataField: "geoStatus",
                text: t("Geo Status"),
                sort: false,
                formatter: (_cell, { geolocationReference }) => {
                    if(geolocationReference?.length > 0) {
                        return (
                            <span>
                                <i className="ri ri-check-line me-1 text-success font-size-20 align-middle" />
                                {t('Provided')}
                            </span>
                        )
                    }

                    return (
                        <span>
                            <i className="ri ri-close-line me-1 text-danger font-size-20 align-middle" />
                            {t('Not Provided')}
                        </span>
                    )
                }
            },
            {
                dataField: "hsCode",
                text: t("HS Code (6-digit)"),
                sort: false
            },
            {
                dataField: "knCode",
                text: t("KN Code (8-digit)"),
                sort: false
            },
            {
                dataField: "taricCode",
                text: t("TARIC Code (10-digit)"),
                sort: false
            },
            {
                dataField: "containerNumber",
                text: t("Container Number"),
                sort: true
            },
            {
                dataField: "shipmentNumber",
                text: t("Shipment Number"),
                sort: true
            },
            {
                dataField: "description",
                text: t('Description'),
                sort: false
            },
            {
                dataField: "actions",
                text: t("Action"),
                sort: false,
                style: {
                    width: '120px'
                },
                formatter: (_cellContent, row) => {
                    const geocledianStatus = (row.geocledian_status || 'PENDING').toLowerCase();

                    return (
                        <div className="actions">
                            {!readOnly && (
                                <>
                                    <Button
                                        color="primary"
                                        aria-label={t('Edit Product')}
                                        outline
                                        className="me-1 border-0"
                                        onClick={() => {
                                            if (isEudrEnabled) {
                                                const searchParams = new URLSearchParams(location.search);
                                                searchParams.set('id', row.id);
                                                searchParams.set('page', 'edit');
                                                history.push({
                                                    search: searchParams.toString()
                                                });
                                            }
                                            else {
                                                setSelectedProductToEdit(row);
                                            }
                                        }}
                                        id={`edit-btn-${row.id}`}
                                    >
                                        <i className="ri-pencil-line font-size-20" />
                                    </Button>

                                    <UncontrolledTooltip 
                                        placement="top" 
                                        target={`edit-btn-${row.id}`}
                                    >
                                        {t('Edit this product’s details')}
                                    </UncontrolledTooltip>
                                </>
                            )}

                            <>
                                <Button
                                    color="primary"
                                    aria-label={t('View Product Detail')}
                                    outline
                                    className="me-1 border-0"
                                    onClick={() => setSelectedProductToShow(row.id)}
                                    id={`view-btn-${row.id}`}
                                >
                                    <i className="ri-eye-line font-size-20" />
                                </Button>

                                <UncontrolledTooltip 
                                    placement="top" 
                                    target={`view-btn-${row.id}`}
                                >
                                    {t('View detailed product information')}
                                </UncontrolledTooltip>
                            </>

                            <>
                                <Button
                                    color="primary"
                                    aria-label={t('View DDS traceability statement details')}
                                    outline
                                    className="me-1 border-0"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(location.search);
                                        searchParams.set('id', row.id);
                                        searchParams.set('page', 'statement');
                                        history.push({
                                            search: searchParams.toString()
                                        });
                                    }}
                                    id={`dds-btn-${row.id}`}
                                >
                                    <i className="ri-map-pin-line font-size-20" />
                                </Button>

                                <UncontrolledTooltip 
                                    placement="top" 
                                    target={`dds-btn-${row.id}`}
                                >
                                    {t('View DDS traceability statement details')}
                                </UncontrolledTooltip>
                            </>

                            <span id={`geo-btn-${row.id}`}>
                                <Button
                                    color="primary"
                                    aria-label={t('View product details from GeoCleidian API')}
                                    outline
                                    className="me-1 border-0"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(location.search);
                                        searchParams.set('id', row.id);
                                        searchParams.set('page', 'geo');
                                        history.push({
                                            search: searchParams.toString()
                                        });
                                    }}
                                    disabled={geocledianStatus !== 'success'}
                                >
                                    <i className="ri-earth-line font-size-20" aria-hidden="true"></i>
                                </Button>

                                <UncontrolledTooltip 
                                    placement="top" 
                                    target={`geo-btn-${row.id}`}
                                >
                                    {t(geocledianStatus === 'success' ? 'View product origin and deforestation risk' : 'Origin and deforestation data are still being analyzed')}.
                                </UncontrolledTooltip>
                            </span>

                            {!readOnly && (
                                <>
                                    <Button
                                        color="danger"
                                        aria-label={t('Delete Product')}
                                        outline
                                        className="border-0"
                                        onClick={() => setSelectedProductToDelete(row)}
                                        id={`delete-btn-${row.id}`}
                                    >
                                        <i className="ri-delete-bin-line font-size-20" />
                                    </Button>

                                    <UncontrolledTooltip 
                                        placement="top" 
                                        target={`delete-btn-${row.id}`}
                                    >
                                        {t('Delete this product from the supplier')}
                                    </UncontrolledTooltip>
                                </>
                            )}
                        </div>
                    )
                }
            }
        ]
    }, [
        t,
        readOnly,
        location.search,
        history
    ]);

    const {
        selectedColumns,
        onColumnsChange
    } = useTableColumnCustomizer({
        defaultSelectedCols: eudrDefaultCols,
        uniqueIdentifier: 'tpdd-admin-supplier-products-cols'
    });

    const visibleColumns = useMemo(() => {
        if (isFeatureEnabledForActiveModule('eudr_activation')) {
            return eudrAllColumns.filter((col) => selectedColumns.includes(col.dataField) || col.locked)
        }

        return [
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                style: {
                    width: '80px'
                }
            },
            {
                dataField: "name",
                text: t("NACE Code"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    return `${cellContent.class} - ${t(cellContent.name)}`
                }
            },
            {
                dataField: "productName",
                text: t("Product Name"),
                sort: true,
                style: {
                    width: '200px'
                }
            },
            {
                dataField: "articleNumber",
                text: t("Article Number"),
                sort: false,
                formatter: (_cell, row) => row?.articleNumber || row?.article_number || '---',
            },
            {
                dataField: "internalSku",
                text: t("Internal SKU"),
                sort: false,
                formatter: (_cell, row) => row?.internalSku || row?.internal_sku || '---',
            },
             ...(isFeatureEnabledForActiveModule('PPWR') ? [{
                dataField: "is_ppwr",
                text: t("PPWR Product"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    if (!cellContent) {
                        return (
                            <span>
                                <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                {t('No')}
                            </span>
                        )
                    }
                    return (
                        <span>
                            <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                            {t('Yes')}
                        </span>
                    )
                }
            }] : []),
            {
                dataField: "description",
                text: t('Description'),
                sort: false,
                style: {
                    width: '200px'
                }
            },
            {
                dataField: "actions",
                text: t("Action"),
                sort: false,
                style: {
                    width: '120px'
                },
                formatter: (_cellContent, row) => {
                    return (
                        <div className="actions">
                            {!readOnly && (
                                <Button
                                    color="primary"
                                    aria-label={t('Edit Product')}
                                    outline
                                    className="me-1 border-0"
                                    onClick={() => setSelectedProductToEdit(row)}
                                >
                                    <i className="ri-pencil-line font-size-20" />
                                </Button>
                            )}

                            <Button
                                color="primary"
                                aria-label={t('View Product Detail')}
                                outline
                                className="me-1 border-0"
                                onClick={() => setSelectedProductToShow(row.id)}
                            >
                                <i className="ri-eye-line font-size-20" />
                            </Button>

                            {!readOnly && (
                                <Button
                                    color="danger"
                                    aria-label={t('Delete Product')}
                                    outline
                                    className="border-0"
                                    onClick={() => setSelectedProductToDelete(row)}
                                >
                                    <i className="ri-delete-bin-line font-size-20" />
                                </Button>
                            )}
                        </div>
                    )
                }
            }
        ]
    }, [
        t,
        eudrAllColumns,
        readOnly,
        selectedColumns
    ]);

    const handleTableFilterChange = useCallback((newFilters) => {
        setFilters((currentState) => {
            return {
                ...currentState,
                ...newFilters,
                pageIndex: 1
            }
        })
    }, []);

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch(type){
            case 'pagination':
                    setFilters({
                        ...filters,
                        pageIndex   :   page,
                        pageSize    :   sizePerPage
                    });
                break;
            case 'sort':
                    setFilters({
                        ...filters,
                        sortField   :   sortField,
                        sortOrder   :   sortOrder.toUpperCase()
                    })
                break;
            default:
                return false;
        }
    }

    const NoDataIndication = () => {
        if (productsFetched && !products?.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p className="text-center mb-0">
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            )
        }

        return <></>
    }

    const isEudrEnabled = isFeatureEnabledForActiveModule('eudr_activation');

    return (
        <Container fluid className="mt-4 mb-4">
            <Row>
                <Col sm='12' className="d-flex justify-content-between flex-row-reverse mb-4">
                    <div className="d-flex justify-content-end">
                        <FilteringComponent
                            onChange={handleTableFilterChange}
                            countries={handleFetchCountriesQuery}
                        />

                        {!readOnly && (
                            <Button
                                color="primary"
                                disabled={loadingSupplier}
                                onClick={() => {
                                    if (isEudrEnabled) {
                                        const searchParams = new URLSearchParams(location.search);
                                        searchParams.set('page', 'create');
                                        history.push({
                                            search: searchParams.toString()
                                        });
                                    }
                                    else {
                                        setShowNewProductModal(true);
                                    }
                                }}
                            >
                                <i className="ri-add-line align-middle me-2" />
                                {t('Add Product')}
                            </Button>
                        )}
                    </div>

                    {isEudrEnabled && (
                        <TableColumnCustomizer 
                            allColumns={eudrAllColumns}
                            visibleColumns={selectedColumns}
                            onChange={onColumnsChange}
                        />
                    )}
                </Col>
                <Col sm='12' className="mb-4">
                    <Card className="shadow-none">
                        <CardBody className="p-0">
                            <PaginationProvider
                                pagination={
                                    paginationFactory({
                                        custom: true,
                                        page: filters.pageIndex,
                                        sizePerPage: filters.pageSize,
                                        totalSize: parseInt(products?.itemsCount || 0),
                                        withFirstAndLast: true,
                                        alwaysShowAllBtns: true,
                                        prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                        nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                        prePageTitle: t('Pre page'),
                                        firstPageTitle: t('First page'),
                                        lastPageTitle: t('Last page'),
                                        showTotal: false,
                                        paginationTotalRenderer: (from, to, size) => {
                                            return (
                                                <span className="react-bootstrap-table-pagination-total">
                                                    {`${t('Total Items')} ${size}`}
                                                </span>
                                            )
                                        },
                                        hideSizePerPage: false,
                                        sizePerPageList: [
                                            {
                                                text: '25',
                                                value: 25
                                            },
                                            {
                                                text: '50',
                                                value: 50
                                            }
                                        ]
                                    })
                                }>
                                {
                                    ({
                                        paginationProps,
                                        paginationTableProps
                                    }) => (
                                        <>
                                            <Row>
                                                <Col sm="12">
                                                    <BootstrapTable
                                                        remote={{
                                                            pagination: true,
                                                            filter: false,
                                                            sort: true,
                                                            cellEdit: false,
                                                            search: false
                                                        }}
                                                        loading={productsAreFetching}
                                                        overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                        onTableChange={handleTableChange}
                                                        defaultSorted={[]}
                                                        keyField={"id"}
                                                        responsive={true}
                                                        bordered={false}
                                                        data={products?.products || []}
                                                        striped={true}
                                                        columns={visibleColumns}
                                                        wrapperClasses="table-responsive"
                                                        classes={"table tpdd-table"}
                                                        headerWrapperClasses={"thead-light"}
                                                        noDataIndication={() => <NoDataIndication />}
                                                        {...paginationTableProps}
                                                    />
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col sm="12">
                                                    <div className="tpdd-pagination-style-1">
                                                        <PaginationTotalStandalone
                                                            {...paginationProps}
                                                        />

                                                        <div>
                                                            <PaginationListStandalone
                                                                {...paginationProps}
                                                            />

                                                            <SizePerPageDropdownStandalone
                                                                {...paginationProps}
                                                            />
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </>
                                    )
                                }

                            </PaginationProvider>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {!readOnly && (
                <>
                    {!isEudrEnabled && (
                        <NewProductModal
                            toggle={() => {
                                if (showNewProductModal) {
                                    setShowNewProductModal(!showNewProductModal)
                                }

                                if (selectedProductToEdit) {
                                    setSelectedProductToEdit(null)
                                }
                            }}
                            show={showNewProductModal || selectedProductToEdit}
                            supplierId={supplierId}
                            industryIds={industryIds}
                            prevData={selectedProductToEdit}
                            onSuccess={() => {
                                refetchProductsList()
                            }}
                        />
                    )}

                    <DeleteProductModal
                        isOpen={!!selectedProductToDelete}
                        toggle={() => setSelectedProductToDelete(null)}
                        onSuccess={() => {
                            refetchProductsList()
                        }}
                        product={selectedProductToDelete}
                    />
                </>
            )}

            <ProductQuickViewModal
                isOpen={!!selectedProductToShow}
                toggle={() => setSelectedProductToShow(null)}
                productId={selectedProductToShow}
            />
        </Container>
    )
}

export default withNamespaces()(
    ProductList
);